import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Subscription, Product, SubscriptionSettings } from '@/types/models.types';
import { ApiError, ApiErrorResponse, NotificationProps, PaywallContextType } from '@/types/util.types';
import {
  ENDPOINTS,
  PUBLIC_API,
  PRIVATE_API,
  ErrorCodes,
  PAYWALL_URL,
  PRIVATE_ENDPOINTS,
  TrialStatus,
} from '@/constants/constants';
import { getTrialEligibility } from '@/utils/getTrialEligibility';
import { useAuth } from './useAuth';

export const PaywallContext = createContext<PaywallContextType | null>(null);
export const usePaywall = () => useContext(PaywallContext);

/** This hook contains all wallet and credit related functionality and handles all requests
 * to the Paywall service.
 */
export const PaywallProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { inProgress } = useMsal();
  const { getAccessTokenOrRefresh } = useAuth();
  const [accessToken, setAccessToken] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<NotificationProps>();
  const [userHasBypass, setUserHasBypass] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  const fetchData = useCallback(
    async (endpoint: string) => {
      const options: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        if (token) options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

        const baseUrl = token ? PRIVATE_API : PUBLIC_API;
        const url = `${baseUrl}${endpoint}`;
        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Gets a list of products available for purchase by the author
   */
  const getAuthorProducts = useCallback(
    async (authorId: number) => {
      try {
        const data = await fetchData(ENDPOINTS.getAuthorProducts(authorId.toString()));
        return data;
      } catch (error: unknown) {
        const errorWithResponse = error as ApiError;
        if (errorWithResponse?.response?.status === 404) {
          console.warn('No wallet found in database for user');
          return;
        }

        if (errorWithResponse?.response?.status >= 500) {
          throw new Error(errorWithResponse.response?.data?.error?.message);
        }

        console.error('Error getting author products:', error);
      }
    },
    [getAccessTokenOrRefresh, fetchData]
  );

  /**
   * Gets a list of authors that the user has notification settings for
   */
  const getNotificationSettingsAuthors = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(PAYWALL_URL);
      if (!token) return [];

      const response = await fetch(PRIVATE_ENDPOINTS.getNotificationSettingsAuthors, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notification settings authors');
      }

      return await response.json();
    } catch (error: unknown) {
      const errorWithResponse = error as ApiError;
      if (errorWithResponse?.response?.status >= 500) {
        throw new Error(errorWithResponse.response?.data?.error?.message);
      }
      console.error('Error getting notification settings authors:', error);
      return [];
    }
  }, [getAccessTokenOrRefresh]);

  /**
   * Gets a list of subscriptions for the current user
   */
  const getUserSubscriptions = useCallback(async () => {
    setIsLoading(true);

    try {
      const token = await getAccessTokenOrRefresh(PAYWALL_URL);
      if (!token) return false;

      const response = await fetch(PRIVATE_ENDPOINTS.getUserSubscriptions, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${token}`,
        },
      });

      const data = await response.json();

      data.sort((a: Subscription, b: Subscription) => {
        return new Date(b.lastPayment).getTime() - new Date(a.lastPayment).getTime();
      });

      setSubscriptions(data);
      return data;
    } catch (error: unknown) {
      const errorWithResponse = error as ApiError;
      if (errorWithResponse?.response?.status >= 500) {
        throw new Error(errorWithResponse.response?.data?.error?.message);
      }

      console.error('Error getting subscriptions for user:', error);
      setSubscriptions([]);
      return;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenOrRefresh]);

  /**
   * Gets user's bypass status
   * i.e. if they are in the paywall bypass or not
   */
  const getUserBypassStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(PAYWALL_URL);
      if (!token) return false;

      const response = await fetch(PRIVATE_ENDPOINTS.getUserBypassStatus, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${token}`,
        },
      });

      const { hasBypass } = await response.json();
      setUserHasBypass(hasBypass);
    } catch (error: unknown) {
      const errorWithResponse = error as ApiError;
      if (errorWithResponse?.response?.status >= 500) {
        throw new Error(errorWithResponse.response?.data?.error?.message);
      }

      console.error("Error getting user's bypass status:", error);
      return;
    }
  }, [getAccessTokenOrRefresh]);

  /**
   * Gets the settings for a subscription
   */
  const getSubscriptionsSettings = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(PAYWALL_URL);
      if (!token) return false;

      const response = await fetch(PRIVATE_ENDPOINTS.getSubscriptionsSettings, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${token}`,
        },
      });

      const data = await response.json();
      return data;
    } catch (error: unknown) {
      const errorWithResponse = error as ApiError;
      if (errorWithResponse?.response?.status >= 500) {
        throw new Error(errorWithResponse.response?.data?.error?.message);
      }

      console.error('Error getting subscriptions settings:', error);
    }
  }, [getAccessTokenOrRefresh]);

  /**
   * Sets the settings for a subscription
   */
  const setSubscriptionSettings = useCallback(
    async (subscriptionId: string, settings: SubscriptionSettings) => {
      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        if (!token) return false;

        const response = await fetch(PRIVATE_ENDPOINTS.setSubscriptionSettings(subscriptionId), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `bearer ${token}`,
          },
          body: JSON.stringify({
            notificationSettings: settings.notificationSettings,
          }),
        });

        if (!response.ok) {
          throw await response.json();
        }

        const data = await response.json();
        return data;
      } catch (error: unknown) {
        const errorWithResponse = error as ApiError;
        if (errorWithResponse?.response?.status >= 500) {
          throw new Error(errorWithResponse.response?.data?.error?.message);
        }

        console.error('Error setting subscription settings:', error);
        return settings;
      }
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Handler for purchasing an article
   */
  const purchaseArticle = useCallback(
    async (articleId: number): Promise<string> => {
      setIsLoading(true);
      setError(undefined);

      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        const response = await fetch(`${PRIVATE_ENDPOINTS.purchaseArticle}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `bearer ${token}`,
          },
          body: JSON.stringify({ articleId, returnUrl: window.location.href }),
        });

        const data = await response.json();
        if (data.id && data.client_secret) {
          setIsLoading(false);
          return data.client_secret;
        }

        if (data.code && data.code === ErrorCodes.AlreadyPurchased) {
          navigate(`/article/${articleId}`);
        } else {
          console.error('Error purchasing article:', data.message);
          throw { code: data.code, message: data.message };
        }

        setIsLoading(false);
        return '';
      } catch (error: unknown) {
        const errorWithCode = error as ApiErrorResponse;
        if ((error as ApiErrorResponse).code) {
          setError({ type: 'error', code: errorWithCode.code, message: errorWithCode.message });
        } else {
          setError({ type: 'error', message: (error as Error).message });
        }
        setIsLoading(false);
        return '';
      }
    },
    [getAccessTokenOrRefresh, navigate]
  );

  /**
   * Validate the Stripe checkout session and find the matching transaction in the database
   */
  const retryBackoff = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const validateCheckout = useCallback(
    async (checkoutSessionId: string): Promise<{ success: boolean; authorId?: string; articleId?: string } | null> => {
      let attempts = 0;
      while (attempts < 3) {
        try {
          const token = await getAccessTokenOrRefresh(PAYWALL_URL);
          if (!token) return null;

          const response = await fetch(`${PRIVATE_ENDPOINTS.validateCheckout}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `bearer ${token}`,
            },
            body: JSON.stringify({ checkoutSessionId }),
          });

          const data = await response.json();

          if (!response.ok) {
            if (data.code === ErrorCodes.DataUnavailable && attempts < 2) {
              attempts++;
              await retryBackoff(4000);
              continue;
            }
            throw data;
          }

          return data;
        } catch (error: unknown) {
          if (attempts >= 2) {
            setError({ type: 'error', message: (error as Error).message });
          }
          return null;
        }
      }
      return null;
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Calls the endpoints to generate a checkout session that
   * will allow the user to subscribe to an author
   */
  const subscribeToAuthor = useCallback(
    async (productId: string, authorId: string) => {
      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        if (!token) return false;

        const response = await fetch(PRIVATE_ENDPOINTS.createSubscription, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `bearer ${token}`,
          },
          body: JSON.stringify({ priceId: productId }),
        });

        const data = await response.json();
        if (data.code && data.code === ErrorCodes.AlreadyPurchased) {
          window.location.assign(`/author/${authorId}`);
        }

        return data.client_secret;
      } catch (error: unknown) {
        const errorWithResponse = error as ApiError;

        if (errorWithResponse?.response?.status >= 500) {
          throw new Error(errorWithResponse.response?.data?.error?.message);
        }

        console.error('Unable to create subscription', error);
        throw errorWithResponse;
      }
    },
    [getAccessTokenOrRefresh]
  );

  /**
   * Cancels a subscription to an author
   */
  const cancelSubscription = useCallback(
    async (subscriptionId: string) => {
      try {
        const token = await getAccessTokenOrRefresh(PAYWALL_URL);
        if (!token) return;

        const response = await fetch(PRIVATE_ENDPOINTS.cancelSubscription, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `bearer ${token}`,
          },
          body: JSON.stringify({ subscriptionId, cancel: true }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message);
        }

        if (data.success) {
          await getUserSubscriptions();
          return data;
        }
      } catch (error: unknown) {
        const errorWithResponse = error as ApiError;

        if (errorWithResponse?.response?.status >= 500) {
          throw new Error(errorWithResponse.response?.data?.error?.message);
        }

        setError({ type: 'error', message: errorWithResponse.response?.data?.error?.message });
        throw errorWithResponse;
      }
    },
    [getAccessTokenOrRefresh, getUserSubscriptions]
  );

  /**
   * Check in the users subscriptions if they have completed the trial for a given author
   */
  const hasUserCompletedTrial = (authorId: number): boolean => {
    return subscriptions.some(
      (sub) => sub?.author?.lookupKey === authorId && sub.trialStatus === TrialStatus.Completed
    );
  };

  /**
   * Determine the paywall mode which is used to display the correct checkout flow.
   */
  const getPaywallMode = useCallback(
    async (product: Product | undefined, authorKey: number) => {
      if (!product) {
        return 'purchase';
      }

      const isTrialEligible = getTrialEligibility(product, subscriptions, authorKey.toString());
      return isTrialEligible ? 'trial' : 'subscribe';
    },
    [subscriptions]
  );

  /**
   * At startup we get the access token that MSAL provides to use in our API calls.
   * TODO: This should be updated to use the accessToken from MSAL
   */
  useEffect(() => {
    const getToken = async () => {
      const token = await getAccessTokenOrRefresh(PAYWALL_URL);
      if (token !== null) {
        setAccessToken(token);
      }
    };

    getToken();
  }, [getAccessTokenOrRefresh, inProgress]);

  /**
   * Fetch the user's wallet when the access token is available
   */
  useEffect(() => {
    if (accessToken) {
      getUserBypassStatus();
      getUserSubscriptions();
    }
  }, [accessToken, getUserSubscriptions]);

  return (
    <PaywallContext.Provider
      value={{
        subscriptions,
        error,
        isLoading,
        userHasBypass,
        purchaseArticle,
        validateCheckout,
        getAuthorProducts,
        getPaywallMode,
        getNotificationSettingsAuthors,
        getSubscriptionsSettings,
        subscribeToAuthor,
        cancelSubscription,
        hasUserCompletedTrial,
        setIsLoading,
        setSubscriptionSettings,
      }}
    >
      {children}
    </PaywallContext.Provider>
  );
};
